import React, {useState} from 'react'
import {useDispatch, useSelector} from "react-redux";
import {FiUser, FiLock, FiEye, FiEyeOff, FiLogIn, FiAlertCircle, FiSun, FiMoon} from "react-icons/fi";
import {GiTurnstile} from "react-icons/gi";
import {apiLoginUser, axiosSetAuthorizationToken} from "../Actions";
import Alert from "../../../components/alerts";
import {setGlobalTheme} from "../../../reducers/palettes";

const BRAND_COLOR = "#004ac6";
const BRAND_COLOR_DARK = "#5b8def";
const BRAND_PANEL_DARK = "#001e4f";

const LoginInput = ({icon, rightIcon, onRightIconClick, ...inputProps}) => {
    return (
        <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-grey-400 dark:text-grey-500">
                {icon}
            </div>
            <input
                {...inputProps}
                className={"w-full h-12 pl-10 " + (rightIcon ? "pr-10" : "pr-4") +
                    " border border-grey-200 dark:border-grey-700 rounded text-sm" +
                    " text-grey-900 dark:text-white bg-white dark:bg-grey-800" +
                    " placeholder-grey-400 dark:placeholder-grey-500" +
                    " focus:outline-none focus:border-blue-600 transition-colors"}
            />
            {rightIcon &&
                <button type="button"
                        onClick={onRightIconClick}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-grey-400 dark:text-grey-500 hover:text-grey-600 dark:hover:text-grey-300 focus:outline-none">
                    {rightIcon}
                </button>
            }
        </div>
    )
}

const LoginPage = () => {

    let [values, setValues] = useState({login: "", password: ""})
    let [showPassword, setShowPassword] = useState(false)
    let [loading, setLoading] = useState(false)
    let [error, setError] = useState(null)
    let dark = useSelector(state => state.palettes?.background === "dark")
    let dispatch = useDispatch()

    const toggleTheme = () => dispatch(setGlobalTheme(dark ? "light" : "dark"))

    const setValue = (name, value) => setValues(v => ({...v, [name]: value}))

    const onLogin = () => {
        if (!values.login || !values.password) {
            setError("Vyplňte přihlašovací jméno i heslo.")
            return;
        }
        setError(null)
        setLoading(true)
        apiLoginUser(values, (data) => {
            setLoading(false)
            axiosSetAuthorizationToken(data.token, dispatch);
        }, error => {
            console.log(error)
            setLoading(false);
            switch (error?.type) {
                case "LOGIN_ERROR":
                    setError("Špatné přihlašovací údaje.");
                    break;
                case "USER_NOT_VERIFED":
                    setError("Účet nebyl dosud verifikován. Klikněte na odkaz v e-mailu.");
                    break;
                default:
                    setError("Došlo k chybě.")
            }
        })
    }

    const onKeyDown = (e) => {
        if (e.key === "Enter") onLogin();
    }

    return (
        <div className={dark ? "dark-mode" : ""}>
        <div className="min-h-screen flex flex-col md:flex-row bg-white dark:bg-grey-900">

            {/* Left: branding */}
            <div className="hidden md:flex md:w-1/2 lg:w-3/5 p-12 flex-col items-center justify-center text-white relative overflow-hidden"
                 style={{backgroundColor: dark ? BRAND_PANEL_DARK : BRAND_COLOR}}>
                <div className="z-10 flex flex-col items-center text-center max-w-lg">
                    <div className="mb-8">
                        <GiTurnstile className="w-full text-center" size={50}/>
                        <img src={'/icons/enigoo-white.svg'} className={'flex items-center justify-center mt-5 mb-2'} width={200}/>
                        <p className="text-sm font-bold uppercase tracking-wider mt-1 text-white text-opacity-75">
                            Turnikety
                        </p>
                    </div>
                    <h2 className="text-2xl font-bold mb-4">Řízení přístupu a odbavovacích turniketů</h2>
                    <p className="text-white text-opacity-75 text-base">
                        Sledujte turnikety, zóny a návštěvníky v reálném čase, na jednom místě.
                    </p>
                </div>
            </div>

            {/* Right: form */}
            <div className="w-full md:w-1/2 lg:w-2/5 flex items-center justify-center p-6 sm:p-12 min-h-screen relative">

                {/*<button type="button"
                        onClick={toggleTheme}
                        title={dark ? "Světlý režim" : "Tmavý režim"}
                        style={{position: "absolute", top: "1rem", right: "1rem", width: "2.25rem", height: "2.25rem"}}
                        className="flex items-center justify-center rounded-full
                                   bg-grey-100 text-grey-600 hover:bg-grey-200
                                   dark:bg-grey-800 dark:text-grey-300 dark:hover:bg-grey-700
                                   transition-colors focus:outline-none">
                    {dark ? <FiSun size={16}/> : <FiMoon size={16}/>}
                </button>*/}

                <div className="w-full max-w-sm">

                    <div className="md:hidden flex flex-col items-center mb-8 text-center">
                        <h1 className="text-2xl font-bold tracking-tight flex items-center"
                            style={{color: dark ? BRAND_COLOR_DARK : BRAND_COLOR}}>
                            <GiTurnstile className="mr-2" size={28}/>
                            ENIGOO
                        </h1>
                        <p className="text-xs font-bold uppercase tracking-wider mt-1"
                           style={{color: dark ? '#94a3b8' : '#505f76'}}>
                            Turnikety
                        </p>
                    </div>

                    <div className="mb-8 text-center md:text-left">
                        <h2 className="text-3xl text-grey-900 dark:text-white font-bold tracking-tight mb-2">Přihlášení</h2>
                        <p className="text-sm text-grey-500 dark:text-grey-400">Vítejte, zadejte prosím své údaje.</p>
                    </div>

                    {error &&
                        <div className="mb-4">
                            <Alert color="bg-red-50 dark:bg-red-900 text-red-600 dark:text-red-200"
                                   rounded={true} borderLeft={true}
                                   icon={<FiAlertCircle size={18} className="mr-2 flex-shrink-0"/>}>
                                <span className="text-sm">{error}</span>
                            </Alert>
                        </div>
                    }

                    <div className="space-y-6">
                        <div className="space-y-2">
                            <label className="block text-sm font-semibold text-grey-700 dark:text-grey-300" htmlFor="login">
                                Přihlašovací jméno
                            </label>
                            <LoginInput
                                id="login"
                                name="login"
                                type="text"
                                icon={<FiUser size={18}/>}
                                placeholder="Zadejte své uživatelské jméno"
                                value={values.login}
                                onChange={(e) => setValue("login", e.target.value)}
                                onKeyDown={onKeyDown}
                            />
                        </div>

                        <div className="space-y-2">
                            <label className="block text-sm font-semibold text-grey-700 dark:text-grey-300" htmlFor="password">
                                Heslo
                            </label>
                            <LoginInput
                                id="password"
                                name="password"
                                type={showPassword ? "text" : "password"}
                                icon={<FiLock size={18}/>}
                                rightIcon={showPassword ? <FiEyeOff size={18}/> : <FiEye size={18}/>}
                                onRightIconClick={() => setShowPassword(s => !s)}
                                placeholder="Zadejte své heslo"
                                value={values.password}
                                onChange={(e) => setValue("password", e.target.value)}
                                onKeyDown={onKeyDown}
                            />
                        </div>

                        <button
                            type="button"
                            onClick={onLogin}
                            disabled={loading}
                            style={{backgroundColor: dark ? BRAND_COLOR_DARK : BRAND_COLOR}}
                            className={"w-full h-12 text-white text-sm font-semibold rounded flex items-center justify-center gap-2 transition-opacity focus:outline-none " +
                                (loading ? "opacity-75" : "hover:opacity-75")}>
                            {loading ? "Pracuji..." : "PŘIHLÁSIT SE"}
                            {!loading && <FiLogIn size={18}/>}
                        </button>
                    </div>
                </div>
            </div>
        </div>
        </div>
    )
}

export default LoginPage
